import React from 'react';
import SparkFormStepper from 'sharedComponents/UI/SparkStepper';
import { Box } from '@mui/material';
import {
  PERSONAL_INFO_FORM_STEPS,
  PERSONAL_INFO_STEPS_CONFIG,
} from 'constants/onBoardingPErsonalForm';
import PersonalBasicInfoForm, {
  type PersonalBasicInfoFormValues,
} from 'components/AuthScreens/Register/PersonalStepperForm/PersonalBasicInfoForm';
import PersonalOverviewForm, {
  type PersonalOverviewFormValues,
} from 'components/AuthScreens/Register/PersonalStepperForm/PersonalOverViewForm';

import { useQueryClient } from 'react-query';
import { useAddPersonalOverview } from 'hooks/PersonalOverview/useAddPersonalOverview';
import { useAddPersonalOverviewKeyObjectives } from 'hooks/PersonalOverview/useAddPersonalOverviewKeyObjectives ';
import { useAddPersonalOverviewMainChallenges } from 'hooks/PersonalOverview/useAddPersonalOverviewMainChallenges ';
import { ON_BOARDING_FORM_STEPS } from 'constants/onBoardingForm';
import ObjectivesForm, {
  ObjectivesFormValues,
} from 'components/AuthScreens/Register/Stepper/ObjectivesForm';
import ChallengesForm, {
  ChallengesFormValues,
} from 'components/AuthScreens/Register/Stepper/ChallengesForm';
import { useGetGenders } from 'hooks/PersonalOverview/useGetGenders';
import { useUserStore } from 'stores';
import { ApplicationUser } from 'types/ApplicationUser';

type CollectiveValuesType = {
  1?: PersonalBasicInfoFormValues;
  2?: PersonalOverviewFormValues;
  3?: ObjectivesFormValues;
  4?: ChallengesFormValues;
};

const INITIAL_FORM_VALUE: CollectiveValuesType = {
  '1': {
    name: '',
    countryID: '',
    occupation: '',
    industryID: '',
  },
};

const hydrateInitialValues = (
  applicationUser: ApplicationUser | undefined
): CollectiveValuesType => {
  if (!applicationUser) {
    return INITIAL_FORM_VALUE;
  }
  const attributes = applicationUser.attributes;

  return {
    ...INITIAL_FORM_VALUE,
    '1': {
      name: attributes.name + ' ' + attributes.surname,
      countryID: '',
      occupation: '',
      industryID: '',
    },
  };
};

type PersonalInfoStepperFormProps = {
  shouldRenderWelcomeText?: boolean;
  onSave: () => void;
  onResetCategory?: () => void;
  categoryID?: string | null;
};

const PersonalInfoStepperForm: React.FC<PersonalInfoStepperFormProps> = ({
  shouldRenderWelcomeText,
  onSave,
  onResetCategory,
  categoryID,
}) => {
  const [activeStep, setActiveStep] = React.useState<number>(1);
  const { applicationUser } = useUserStore();
  const collectiveValues = React.useRef<CollectiveValuesType | null>(
    hydrateInitialValues(applicationUser)
  );

  const queryClient = useQueryClient();
  const { mutateAsync: addPersonalOverview } = useAddPersonalOverview();
  const { mutateAsync: addPersonalOverviewKeyObjectives } =
    useAddPersonalOverviewKeyObjectives();
  const { mutateAsync: addPersonalOverviewMainChallenges } =
    useAddPersonalOverviewMainChallenges();
  const { dropdownOptions } = useGetGenders();

  const setBasicInfoCollection = (formValues: PersonalBasicInfoFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 1: formValues };
  };
  const setOverViewCollection = (formValues: PersonalOverviewFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 2: formValues };
  };
  const setObjectivesCollection = (formValues: ObjectivesFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 3: formValues };
  };
  const setChallengesCollection = (formValues: ChallengesFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 4: formValues };
  };

  const handleSubmit = async () => {
    if (!collectiveValues.current) {
      return;
    }

    const { name, industryID, countryID, occupation } =
      collectiveValues.current[1] || {};
    const { dateofBirth, genderID, about } = collectiveValues.current[2] || {};
    const { objectiveIDs } = collectiveValues.current[3] || {};
    const { challengesIDs } = collectiveValues.current[4] || {};

    const date = new Date(dateofBirth || '');

    if (
      !objectiveIDs?.length ||
      !challengesIDs?.length ||
      !name ||
      !countryID ||
      !industryID ||
      !occupation
    ) {
      console.error('Missing required fields');
      return;
    }

    try {
      const response = await addPersonalOverview({
        name,
        countryID: countryID ? +countryID : undefined,
        businessIndustryID: industryID ? +industryID : undefined,
        occupation,
        dateofBirth: date.toISOString(),
        genderID: genderID ? +genderID : undefined,
        about,
      });

      const personalOverviewID = response?.data?.id;
      if (!personalOverviewID) {
        throw new Error('Personal Overview ID (workspaceID) not returned');
      }

      const objectivePromises = objectiveIDs.map((objectiveID) =>
        addPersonalOverviewKeyObjectives({
          keyObjectiveID: +objectiveID,
          personalOverviewID,
        })
      );

      const challengePromises = challengesIDs.map((challengeID) =>
        addPersonalOverviewMainChallenges({
          mainChallengeID: +challengeID,
          personalOverviewID,
        })
      );

      await Promise.all([...objectivePromises, ...challengePromises]);
      queryClient.invalidateQueries('ApplicationUser');
      queryClient.invalidateQueries(['workspaces']);

      onSave();
    } catch (error) {
      console.error('Error during submission', error);
    }
  };

  const nextStepHandler = async () => {
    const currentStepConfig = PERSONAL_INFO_STEPS_CONFIG.find(
      ({ formKey }) => formKey === activeStep
    );

    if (!currentStepConfig) {
      return;
    }

    const { nextStepFormKey } = currentStepConfig || {};
    if (nextStepFormKey) {
      setActiveStep(nextStepFormKey);
      return;
    }

    handleSubmit();
  };

  const previousStepHandler = () => {
    if (activeStep === 1) {
      onResetCategory && onResetCategory();
      return;
    }
    setActiveStep((prev) => (prev === 1 ? prev : prev - 1));
  };

  const getCurrentStep = () => {
    switch (activeStep) {
      case PERSONAL_INFO_FORM_STEPS.PERSONAL_BASIC_INFO_ID:
        return (
          <PersonalBasicInfoForm
            shouldRenderWelcomeText={shouldRenderWelcomeText}
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            personalInfoCollection={collectiveValues.current?.[1]}
            setPersonalInfoCollection={setBasicInfoCollection}
          />
        );
      case PERSONAL_INFO_FORM_STEPS.PERSONAL_OVER_VIEW_ID:
        return (
          <PersonalOverviewForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            overViewCollection={collectiveValues.current?.[2]}
            setOverViewCollection={setOverViewCollection}
            dropdownOptions={dropdownOptions}
          />
        );
      case ON_BOARDING_FORM_STEPS.OBJECTIVES_FORM_ID:
        return (
          <ObjectivesForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            objectivesCollection={collectiveValues.current?.[3]}
            setObjectivesCollection={setObjectivesCollection}
            categoryID={categoryID}
          />
        );
      case ON_BOARDING_FORM_STEPS.CHALLENGES_FORM_ID:
        return (
          <ChallengesForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            challengesCollection={collectiveValues.current?.[4]}
            setChallengesCollection={setChallengesCollection}
            categoryID={categoryID}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '600px !important' }} marginTop={3}>
      <SparkFormStepper
        stepIDs={PERSONAL_INFO_STEPS_CONFIG.map((step) => step.formKey)}
        activeStep={activeStep}
      />
      <div>{getCurrentStep()}</div>
    </Box>
  );
};

export default PersonalInfoStepperForm;
